// src/components/dnl/ContextPresetSelector.tsx
/**
 * Context Preset Selector Component
 * Phase 6: Context Presets
 * 
 * Dropdown selector for common DNL scenarios with descriptions
 * Based on DNL Enhancement Specification v1.0, Section 3.8
 */

import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  ChefHat, 
  Ruler, 
  Percent, 
  Divide, 
  Maximize2, 
  Coins, 
  Settings,
  HelpCircle,
  Check,
} from 'lucide-react';
import { RootState } from '../../types/redux';
import { 
  applyContextPreset, 
  selectCurrentPreset, 
  selectShowPresetDescriptions 
} from '../../store/slices/dnlToolsSlice';
import { setScaleFactor, setInheritFormatFromPrimary } from '../../store/slices/secondaryLineSlice';
import { 
  setFormatType as setPrimaryFormatType,
  updateNumberFormatProperty as updatePrimaryNumberFormatProperty,
} from '../../store/slices/numberLinesSlice';
import {
  setFormatType as setSecondaryFormatType,
  updateNumberFormatProperty as updateSecondaryNumberFormatProperty,
} from '../../store/slices/secondaryLineSlice';
import { getAvailablePresets, CONTEXT_PRESETS } from '../../lib/presets';
import type { DNLContextPreset } from '../../types/dnl';
import type { NumberFormatConfig } from '../../types/redux';

// =============================================================================
// TYPES
// =============================================================================

export interface ContextPresetSelectorProps {
  isDark?: boolean;
  compact?: boolean;
}

// =============================================================================
// ICON MAPPING
// =============================================================================

const PRESET_ICONS: Record<string, React.ComponentType<{ size?: number }>> = {
  ChefHat,
  Ruler,
  Percent,
  Divide,
  Maximize2,
  Coins,
  Settings,
};

// =============================================================================
// COMPONENT
// =============================================================================

export const ContextPresetSelector: React.FC<ContextPresetSelectorProps> = ({
  isDark = false,
  compact = false,
}) => {
  const dispatch = useDispatch();
  
  const currentPreset = useSelector(selectCurrentPreset);
  const showDescriptions = useSelector(selectShowPresetDescriptions);
  const [isOpen, setIsOpen] = useState(false);
  
  const presets = getAvailablePresets();
  
  // Handle preset selection
  const handleSelectPreset = useCallback((presetId: DNLContextPreset) => {
    const config = CONTEXT_PRESETS[presetId];
    
    // Apply the preset to DNL tools (labels)
    dispatch(applyContextPreset({ presetId, applyScaleFactor: true }));
    
    // Disable inheritance so secondary line can have its own format
    dispatch(setInheritFormatFromPrimary(false));
    
    // Apply primary line format
    dispatch(setPrimaryFormatType(config.primaryFormat.type));
    Object.entries(config.primaryFormat).forEach(([key, value]) => {
      if (key !== 'type' && value !== undefined) {
        dispatch(updatePrimaryNumberFormatProperty({ 
          key: key as keyof NumberFormatConfig, 
          value 
        }));
      }
    });
    
    // Apply secondary line format
    dispatch(setSecondaryFormatType(config.secondaryFormat.type));
    Object.entries(config.secondaryFormat).forEach(([key, value]) => {
      if (key !== 'type' && value !== undefined) {
        dispatch(updateSecondaryNumberFormatProperty({ 
          key: key as keyof NumberFormatConfig, 
          value 
        }));
      }
    });
    
    // Apply scale factor if suggested
    if (config.suggestedScaleFactor) {
      dispatch(setScaleFactor({
        n: config.suggestedScaleFactor.numerator,
        d: config.suggestedScaleFactor.denominator,
      }));
    }
    
    setIsOpen(false);
  }, [dispatch]);
  
  const currentConfig = CONTEXT_PRESETS[currentPreset];
  const IconComponent = PRESET_ICONS[currentConfig.icon || 'Settings'];
  
  return (
    <div className="relative">
      {/* Selector Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3 py-2 rounded border transition-colors ${
          isDark
            ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-650'
            : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
        }`}
      >
        <div className="flex items-center gap-2">
          <IconComponent size={16} className="text-blue-500" />
          <span className="text-sm font-medium">{currentConfig.name}</span>
        </div>
        <div className="flex items-center gap-1">
          {showDescriptions && (
            <HelpCircle size={14} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
          )}
          <svg
            className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      
      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
            style={{ pointerEvents: 'auto' }}
          />
          
          {/* Menu */}
          <div
            className={`absolute top-full left-0 right-0 mt-1 rounded-lg shadow-lg border z-50 max-h-96 overflow-y-auto ${
              isDark
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
            }`}
            style={{ position: 'absolute', zIndex: 50 }}
          >
            {presets.map(({ id, config }) => {
              const Icon = PRESET_ICONS[config.icon || 'Settings'];
              const isSelected = id === currentPreset;
              
              return (
                <button
                  key={id}
                  onClick={() => handleSelectPreset(id)}
                  className={`w-full px-3 py-2.5 text-left transition-colors ${
                    isSelected
                      ? isDark
                        ? 'bg-blue-600/20 text-blue-400'
                        : 'bg-blue-50 text-blue-600'
                      : isDark
                        ? 'hover:bg-gray-700 text-white'
                        : 'hover:bg-gray-50 text-gray-900'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Icon size={18} className={isSelected ? 'text-blue-500' : isDark ? 'text-gray-400' : 'text-gray-500'} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{config.name}</span>
                        {isSelected && <Check size={14} className="text-blue-500" />}
                      </div>
                      {showDescriptions && (
                        <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {config.description}
                        </p>
                      )}
                      {config.suggestedScaleFactor && (
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          Scale: ×{config.suggestedScaleFactor.numerator}/{config.suggestedScaleFactor.denominator}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ContextPresetSelector;

